<template>
  <AppImagePreview
    :url="image.url"
    class="tw-p-6 tw-bg-gray-700 tw-shadow-inner tw-flex tw-rounded tw-max-h-[300px]"
    :cover="false"
  />

  <div class="tw-grid tw-gap-x-6 tw-gap-y-3 tw-grid-cols-6 tw-mt-6">
    <div class="form-group tw-col-span-6">
      <label class="control-label">
        Titel
        <input v-model="image.title" class="form-control" />
      </label>
    </div>
    <div class="form-group tw-col-span-6">
      <label class="control-label">
        Beschreibung
        <textarea
          ref="descriptionEl"
          v-model="image.description"
          rows="1"
          class="form-control"
        ></textarea>
      </label>
    </div>
    <div class="form-group tw-col-span-6 lg:tw-col-span-2">
      <label class="control-label">
        Lizenz
        <select v-model="image.licenseId" class="form-control">
          <option v-for="license in licenses" :key="license.id" :value="license.id">
            {{ license.name }}
          </option>
        </select>
      </label>
    </div>
    <div class="form-group tw-col-span-6 lg:tw-col-span-2">
      <label class="control-label">
        Autor*in
        <input
          v-model="image.authorName"
          class="form-control"
          :required="currentLicense.needsAuthor"
        />
      </label>
    </div>
    <div class="form-group tw-col-span-6 lg:tw-col-span-2 !tw-flex tw-items-center">
      <label class="tw-inline-flex tw-items-center tw-gap-2 tw-mb-0">
        <input
          v-model="image.isUseExplicitlyGrantedByAuthor"
          type="checkbox"
          :required="currentLicense.requiresExpressPermissionForPublication"
        />
        Autor:in erlaubt Veröffentlichung
      </label>
    </div>
    <div class="form-group tw-col-span-6">
      <label class="control-label">
        Quelle
        <input v-model="image.originUrl" type="url" class="form-control" />
      </label>
      <p class="form-text text-muted tw-text-sm tw-mb-0">
        Falls das Bild aus dem Internet herunterladen wurde, sollte hier ein Link zur Quelle
        angegeben werden.
      </p>
    </div>
  </div>

  <p
    v-if="imageUseError"
    class="tw-mt-3 tw-mb-0 tw-bg-rose-600 tw-p-3 tw-rounded tw-text-white"
    role="alert"
  >
    Das Bild kann mit den aktuell hinterlegten Daten nicht in der Mediathek angezeigt werden.
    {{ imageUseError }}
  </p>
</template>

<script lang="ts" setup>
import { useTextareaAutosize } from '@vueuse/core'
import { computed } from 'vue'
import type { Image, License } from '../types'
import AppImagePreview from './AppImagePreview.vue'
import { useUpdatableState } from './util'

const props = defineProps<{
  modelValue: Image
  licenses: License[]
}>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: Image): void
}>()
const image = useUpdatableState<Image>(
  computed(() => props.modelValue),
  (image) => emit('update:modelValue', image),
  (image) => ({ ...image }),
)
const currentLicense = computed(
  () => props.licenses.find((license) => license.id === image.value.licenseId) as License,
)
const imageUseError = computed<string | undefined>(() => {
  if (
    currentLicense.value.requiresExpressPermissionForPublication &&
    !image.value.isUseExplicitlyGrantedByAuthor
  ) {
    return 'Es braucht eine explizite Zustimmung zur Veröffentlichung seitens des:der Autor:in.'
  } else if (currentLicense.value.needsAuthor && !image.value.authorName.trim()) {
    return 'Die Lizenz erfordert die Nennung des:der Autor:in. Bitte gib deren Namen an.'
  }

  return undefined
})

const { textarea: descriptionEl } = useTextareaAutosize({ watch: () => image.value.description })
</script>

<style lang="postcss" scoped>
.form-group,
.control-label {
  @apply tw-mb-0;
  @apply tw-block;
}
</style>
